import { useState } from 'react';
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view';
import { Box, Chip, Fab, IconButton, Typography } from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import PropTypes from 'prop-types';
import Tooltip from '@mui/material/Tooltip';
import { useIntl } from 'react-intl';

export function StructuredOutputTree({
  items,
  onSelectItem,
  onAddItem,
  onDeleteItem,
  onAddRootItem
}) {
  const { formatMessage: msg } = useIntl();
  const [expanded, setExpanded] = useState([]);
  const [selectedItem, setSelectedItem] = useState(items[0]?.id || '');

  const handleSelect = (event, nodeId) => {
    onSelectItem(nodeId);
    setSelectedItem(nodeId);
  };

  const handleAddClick = (event, nodeId) => {
    event.stopPropagation();
    const newItemId = onAddItem(nodeId);
    setExpanded((prev) => {
      if (!prev.includes(nodeId)) {
        return [...prev, nodeId];
      }
      return prev;
    });
    setSelectedItem(newItemId);
  };

  const handleAddRootItem = () => {
    const newRootItemId = onAddRootItem();
    setSelectedItem(newRootItemId);
  };

  const handleDeleteClick = (event, nodeId) => {
    event.stopPropagation();
    onDeleteItem(nodeId);
  };

  const handleNodeToggle = (event, nodeIds) => {
    setExpanded(nodeIds);
  };

  const renderTree = (nodes) => {
    if (!nodes || !nodes.length) return null;

    return nodes.map((node) => (
      <TreeItem
        key={node.id}
        label={
          <Box sx={{ display: 'flex', alignItems: 'center', p: 0.5 }}>
            <Typography variant="body2">{node.name}</Typography>
            <Chip
              label={msg({ id: `field.output.type.${node.type}.label` })}
              size="small"
              color={getTypeColor(node.type)}
              sx={{
                ml: 1,
                color: 'white !important'
              }}
            />
            <Typography
              variant="caption"
              sx={{
                ml: 1,
                fontStyle: 'italic',
                flex: 1
              }}
            >
              {node.description}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}>
              {(node.type === 'object' || node.type === 'array') && (
                <Tooltip title={msg({ id: 'tooltip.add' })}>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={(e) => handleAddClick(e, node.id)}
                    sx={{ p: 0.5 }}
                  >
                    <AddCircleOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}

              {node.id !== 'root' && (
                <Tooltip title={msg({ id: 'tooltip.delete' })}>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={(e) => handleDeleteClick(e, node.id)}
                    sx={{ p: 0.5 }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
        }
        itemId={node.id}
      >
        {node.children && node.children.length > 0 ? renderTree(node.children) : null}
      </TreeItem>
    ));
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'string':
        return 'primary';
      case 'number':
      case 'integer':
        return 'success';
      case 'boolean':
        return 'warning';
      case 'object':
        return 'secondary';
      case 'array':
        return 'info';
      default:
        return 'default';
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '94%',
        position: 'relative',
        border: '1px solid #e0e0e0'
      }}
    >
      <SimpleTreeView
        expandedItems={expanded}
        onExpandedItemsChange={handleNodeToggle}
        onSelectedItemsChange={handleSelect}
        selectedItems={selectedItem}
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          p: 1,
          borderRadius: 1
        }}
      >
        {renderTree(items)}
      </SimpleTreeView>
      <Tooltip title={msg({ id: 'tooltip.add.root' })}>
        <Fab
          size="small"
          color="primary"
          aria-label="add"
          onClick={handleAddRootItem}
          sx={{
            position: 'absolute',
            bottom: 16,
            right: 16,
            zIndex: 1
          }}
        >
          <AddIcon fontSize="medium" />
        </Fab>
      </Tooltip>
    </Box>
  );
}

StructuredOutputTree.propTypes = {
  items: PropTypes.array.isRequired,
  onSelectItem: PropTypes.func.isRequired,
  onAddItem: PropTypes.func.isRequired,
  onDeleteItem: PropTypes.func.isRequired,
  onAddRootItem: PropTypes.func.isRequired
};
